import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  useTheme,
  Box,
  IconButton,
} from '@mui/material';
import { Save as SaveIcon, X as CloseIcon } from 'lucide-mui';

import { addTemplate } from '../templateSlice';
import { generateTitle } from '../../../services/templateService';
import useCustomTemplateForm from '../hooks/useCustomTemplateForm';

interface CustomPromptDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (customTemplate: string, templateId?: string) => void;
}

const CustomPromptDialog: React.FC<CustomPromptDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const { formState, handleChange, resetForm } = useCustomTemplateForm();
  const [isSaving, setIsSaving] = useState(false);
  const theme = useTheme();

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleGenerateAndSave = async () => {
    if (!formState.customTemplate.trim()) return;

    setIsSaving(true);
    try {
      let finalTitle = formState.title.trim();
      if (!finalTitle) {
        finalTitle = await generateTitle(formState.customTemplate);
      }

      const template = {
        title: finalTitle,
        description: formState.description.trim() || undefined,
        prompt: formState.customTemplate.trim(),
      };

      const resultAction = await dispatch(addTemplate(template));
      if (addTemplate.fulfilled.match(resultAction as any)) {
        const newTemplate = (resultAction as any).payload;
        onSubmit(formState.customTemplate.trim(), newTemplate.id);
        handleClose();
      }
    } catch (error) {
      console.error('Failed to save template:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const canSave = formState.customTemplate.trim();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        elevation: 0,
        sx: {
          m: 2,
          borderRadius: 2,
          maxHeight: 'calc(100% - 32px)',
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ width: '100%', pr: 2 }}>
          <TextField
            name="title"
            value={formState.title}
            onChange={handleChange}
            placeholder="Template name"
            variant="standard"
            fullWidth
            autoFocus
            inputProps={{
              style: {
                fontSize: '1.25rem',
                fontWeight: 500,
              },
            }}
            sx={{
              '& .MuiInput-underline:before': {
                borderBottomColor: theme.palette.divider,
              },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                borderBottomColor: theme.palette.divider,
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              ml: 0.5,
              mt: 0.5,
              display: 'block',
              color: theme.palette.text.disabled,
            }}
          >
            Leave blank for AI to generate a name
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          aria-label="close"
          sx={{ ml: 2, mt: -0.5 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, pt: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create a template to extract information from your videos
        </Typography>

        <TextField
          name="customTemplate"
          value={formState.customTemplate}
          onChange={handleChange}
          label="AI instructions"
          type="text"
          fullWidth
          multiline
          rows={3}
          placeholder="Example: Create a summary that includes key decisions, action items with owners, and important discussion points"
          required
          variant="outlined"
          sx={{ mb: 3 }}
        />

        <TextField
          name="description"
          value={formState.description}
          onChange={handleChange}
          label="Notes (optional)"
          type="text"
          fullWidth
          multiline
          rows={2}
          placeholder="Add context about when to use this template"
          variant="outlined"
          size="small"
          sx={{
            '& .MuiInputBase-root': {
              background: theme.palette.action.hover,
            },
          }}
        />
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}
      >
        <Button
          onClick={handleClose}
          sx={{
            color: theme.palette.text.secondary,
            minWidth: 100,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleGenerateAndSave}
          variant="contained"
          disableElevation
          disabled={!canSave || isSaving}
          startIcon={<SaveIcon />}
          sx={{
            minWidth: 100,
            px: 3,
          }}
        >
          {isSaving ? 'Saving...' : 'Save template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomPromptDialog;
